import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_MACHINES,
  COL_PRODUCTION_WORKERS,
  COL_SCHEDULE_SLOTS,
  COL_SHIFTS,
  COL_TIMESHEET_ENTRIES,
} from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import type { CsvColumn } from '../../lib/exportCsv'
import { pb } from '../../pocketbase'
import { useAuth } from '../../context/AuthContext'
import { formatMachineLabel } from '../../lib/machines'
import { formatQty, shiftDurationHours } from '../../lib/scheduleCalculations'
import { fetchCalendarMap } from '../../lib/loadProductionCalendar'
import { isWorkerWorkDay, resolvePlantIsWorking } from '../../lib/productionCalendar'
import { brigadeNotSetHint, brigadeShiftMismatchHint, resolveWorkingBrigade } from '../../lib/shiftRotation'
import { isRotationShift, isShift5_2 } from '../../lib/shifts'
import {
  buildTimesheetEntryCreate,
  normalizeBool,
  timesheetEntryUpdatePatch,
} from '../../lib/pbRecordDefaults'
import {
  findScheduleSlotForMachine,
  machineOccupiedByOther,
  suggestDefaultMachineId,
} from '../../lib/timesheetMachine'
import {
  clearOtherRotationShiftPresence,
  clearOtherShiftPresence,
  isReplacementOnShift,
} from '../../lib/timesheetShiftPresence'
import type {
  MachineRecord,
  ProductionWorkerRecord,
  ScheduleSlotRecord,
  ShiftRecord,
  TimesheetEntryRecord,
} from '../../types/records'
import { Alert, Button } from '../../components/ui'
import {
  btn,
  cn,
  inputClass,
  loadingClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'

function dateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function TimesheetsPage() {
  const { user, can } = useAuth()
  const canWrite = can(PERMISSION_KEYS.TIMESHEETS_WRITE)
  const canAllShifts = can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_ALL_SHIFTS)

  const [workDate, setWorkDate] = useState(() => dateInputValue(new Date()))
  const [shiftId, setShiftId] = useState(() =>
    canAllShifts ? '' : (typeof user?.assignedShift === 'string' ? user.assignedShift : ''),
  )
  const [shiftTouched, setShiftTouched] = useState(false)
  const [shifts, setShifts] = useState<ShiftRecord[]>([])
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [daySlots, setDaySlots] = useState<ScheduleSlotRecord[]>([])
  const [workers, setWorkers] = useState<ProductionWorkerRecord[]>([])
  const [entries, setEntries] = useState<TimesheetEntryRecord[]>([])
  const [allDayEntries, setAllDayEntries] = useState<TimesheetEntryRecord[]>([])
  const [pendingReplacementIds, setPendingReplacementIds] = useState<string[]>([])
  const [calendarMap, setCalendarMap] = useState(
    () => new Map<string, { isWorking: boolean; workingBrigade?: string }>(),
  )
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [massSaving, setMassSaving] = useState(false)

  const selectedShift = useMemo(() => shifts.find((s) => s.id === shiftId) ?? null, [shifts, shiftId])

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const dayStart = `${workDate} 00:00:00`
      const dayEnd = `${workDate} 23:59:59`

      const [sList, wList, mList, calMap] = await Promise.all([
        pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({ sort: 'sort' }),
        pb.collection(COL_PRODUCTION_WORKERS).getFullList<ProductionWorkerRecord>({
          filter: 'active = true',
          expand: 'defaultShift,defaultMachine',
          sort: 'name',
        }),
        pb.collection(COL_MACHINES).getFullList<MachineRecord>({ sort: 'type,sort,number' }),
        fetchCalendarMap(workDate, workDate),
      ])
      setShifts(sList.filter((s) => s.active))
      setWorkers(wList)
      setMachines(mList)
      setCalendarMap(calMap)

      let slotFilter = `plannedStart <= "${dayEnd}" && plannedEnd >= "${dayStart}" && status != "cancelled"`
      if (shiftId) slotFilter += ` && shift="${shiftId}"`
      const slotList = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
        filter: slotFilter,
        sort: 'plannedStart',
      })
      setDaySlots(slotList)

      let entryFilter = `workDate >= "${dayStart}" && workDate <= "${dayEnd}"`
      const dayFilter = entryFilter
      if (shiftId) entryFilter += ` && shift="${shiftId}"`

      const [eList, dayList] = await Promise.all([
        pb.collection(COL_TIMESHEET_ENTRIES).getFullList<TimesheetEntryRecord>({
          filter: entryFilter,
          expand: 'worker,shift,machine',
          sort: 'worker',
        }),
        pb.collection(COL_TIMESHEET_ENTRIES).getFullList<TimesheetEntryRecord>({
          filter: dayFilter,
          expand: 'worker,shift',
          sort: 'worker',
        }),
      ])
      setEntries(eList)
      setAllDayEntries(dayList)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки табеля'))
      setEntries([])
      setAllDayEntries([])
      setDaySlots([])
    } finally {
      setLoading(false)
    }
  }, [workDate, shiftId])

  useEffect(() => {
    setPendingReplacementIds([])
    setShiftTouched(false)
  }, [workDate])

  useEffect(() => {
    if (loading) return
    if (shiftTouched) return
    const brigade = resolveWorkingBrigade(workDate, calendarMap)
    if (brigade) {
      setShiftId(brigade)
      return
    }
    if (!shiftId && shifts.length > 0 && !canAllShifts && user?.assignedShift) {
      setShiftId(user.assignedShift)
    } else if (!shiftId && shifts.length > 0) {
      const firstRotation = shifts.find((s) => isRotationShift(s))
      setShiftId(firstRotation?.id ?? shifts[0].id)
    }
  }, [loading, shifts, shiftId, canAllShifts, user?.assignedShift, workDate, calendarMap, shiftTouched])

  useEffect(() => {
    void load()
  }, [load])

  const entryByWorker = useMemo(() => {
    const map = new Map<string, TimesheetEntryRecord>()
    for (const e of entries) {
      const wid = typeof e.worker === 'string' ? e.worker : ''
      if (wid) map.set(wid, e)
    }
    return map
  }, [entries])

  const timesheetShiftByWorker = useMemo(() => {
    const map = new Map<string, string>()
    for (const e of allDayEntries) {
      const wid = typeof e.worker === 'string' ? e.worker : ''
      if (wid && e.shift) map.set(wid, e.shift)
    }
    return map
  }, [allDayEntries])

  const shiftsById = useMemo(() => new Map(shifts.map((s) => [s.id, s])), [shifts])

  const plantWorking = useMemo(
    () => resolvePlantIsWorking(workDate, calendarMap),
    [workDate, calendarMap],
  )

  const workingBrigadeId = useMemo(
    () => resolveWorkingBrigade(workDate, calendarMap),
    [workDate, calendarMap],
  )

  const brigadeHint = useMemo(() => {
    if (loading) return null
    return brigadeNotSetHint(workDate, calendarMap)
  }, [loading, workDate, calendarMap])

  const shiftMismatchHint = useMemo(() => {
    if (loading || !shiftId) return null
    return brigadeShiftMismatchHint(workDate, shiftId, calendarMap, shiftsById)
  }, [loading, workDate, shiftId, calendarMap, shiftsById])

  const rotationShiftMismatch =
    Boolean(
      workingBrigadeId &&
        shiftId &&
        workingBrigadeId !== shiftId &&
        selectedShift &&
        isRotationShift(selectedShift),
    )

  const shiftWorkers = useMemo(() => {
    if (!shiftId) return workers
    return workers.filter(
      (w) =>
        w.defaultShift === shiftId &&
        isWorkerWorkDay(workDate, w, shiftsById, calendarMap, timesheetShiftByWorker),
    )
  }, [workers, shiftId, workDate, shiftsById, calendarMap, timesheetShiftByWorker])

  const rotationShiftIds = useMemo(
    () => shifts.filter((s) => isRotationShift(s)).map((s) => s.id),
    [shifts],
  )

  const otherRotationPresentCount = useMemo(() => {
    if (!shiftId || !selectedShift || !isRotationShift(selectedShift)) return 0
    return allDayEntries.filter(
      (e) => e.present && e.shift && e.shift !== shiftId && rotationShiftIds.includes(e.shift),
    ).length
  }, [allDayEntries, shiftId, selectedShift, rotationShiftIds])

  const displayedWorkers = useMemo(() => {
    if (!shiftId) return shiftWorkers
    const rosterIds = new Set(shiftWorkers.map((w) => w.id))
    const fromEntries = workers.filter((w) => {
      if (rosterIds.has(w.id)) return false
      return allDayEntries.some(
        (e) => typeof e.worker === 'string' && e.worker === w.id && e.shift === shiftId,
      )
    })
    const pending = workers.filter(
      (w) => pendingReplacementIds.includes(w.id) && !rosterIds.has(w.id),
    )
    const merged = [...shiftWorkers]
    for (const w of [...fromEntries, ...pending]) {
      if (!merged.some((x) => x.id === w.id)) merged.push(w)
    }
    return merged
  }, [shiftWorkers, shiftId, workers, allDayEntries, pendingReplacementIds])

  const replacementCandidates = useMemo(() => {
    if (!shiftId) return []
    const shown = new Set(displayedWorkers.map((w) => w.id))
    return workers
      .filter(
        (w) =>
          w.defaultShift &&
          w.defaultShift !== shiftId &&
          !shown.has(w.id) &&
          isWorkerWorkDay(workDate, w, shiftsById, calendarMap, timesheetShiftByWorker),
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [workers, shiftId, displayedWorkers, workDate, shiftsById, calendarMap, timesheetShiftByWorker])

  const rotationBrigadeUnset =
    selectedShift &&
    isRotationShift(selectedShift) &&
    plantWorking &&
    !workingBrigadeId

  const timesheetDayHint = useMemo(() => {
    if (shiftMismatchHint) return shiftMismatchHint
    if (!plantWorking) return 'День отмечен в календаре как простой — табель для смен 2/2 не ведётся.'
    if (brigadeHint && selectedShift && isRotationShift(selectedShift)) return brigadeHint
    if (!selectedShift) return null
    if (isShift5_2(selectedShift) && shiftWorkers.length === 0) {
      return 'Для смены 5/2 в этот день табель не ведётся (выходной по графику будни).'
    }
    return null
  }, [shiftMismatchHint, plantWorking, brigadeHint, selectedShift, shiftWorkers.length])

  const emptyRosterHint = useMemo(() => {
    if (loading) return 'Загрузка состава…'
    if (shiftMismatchHint) return shiftMismatchHint
    if (rotationBrigadeUnset) {
      return 'Сначала укажите рабочую бригаду в производственном календаре на этот день.'
    }
    return 'Нет сотрудников для этой смены.'
  }, [loading, shiftMismatchHint, rotationBrigadeUnset])

  const timesheetCsvColumns: CsvColumn<TimesheetEntryRecord>[] = useMemo(
    () => [
      { header: 'Сотрудник', value: (e) => e.expand?.worker?.name ?? '' },
      { header: 'Должность', value: (e) => e.expand?.worker?.position ?? '' },
      { header: 'Явка', value: (e) => (e.present ? 'да' : 'нет') },
      { header: 'Часы', value: (e) => Number(e.hours) || 0 },
      {
        header: 'Станок',
        value: (e) => {
          const m = e.expand?.machine
          return m ? formatMachineLabel(m) : '—'
        },
      },
    ],
    [],
  )

  function resolveMachineFields(
    machineId: string,
  ): { machine: string; scheduleSlot: string } {
    if (!machineId) return { machine: '', scheduleSlot: '' }
    const slot = findScheduleSlotForMachine(machineId, shiftId, workDate, daySlots)
    return { machine: machineId, scheduleSlot: slot?.id ?? '' }
  }

  async function massFillShift() {
    if (!canWrite || !shiftId) {
      setErr('Выберите смену для массового заполнения.')
      return
    }
    if (rotationBrigadeUnset) {
      setErr('Укажите рабочую бригаду 2/2 в календаре на этот день.')
      return
    }
    const shift = selectedShift
    const defaultHours = shift ? shiftDurationHours(shift.startTime, shift.endTime) : 8

    setMassSaving(true)
    setErr('')
    try {
      if (selectedShift && isRotationShift(selectedShift)) {
        await clearOtherRotationShiftPresence(shiftId, rotationShiftIds, allDayEntries)
      }
      for (const w of shiftWorkers) {
        await clearOtherShiftPresence(w.id, shiftId, allDayEntries)
        const existing = entryByWorker.get(w.id)
        let machineId = existing?.machine || ''
        if (!machineId) {
          machineId = suggestDefaultMachineId(w, shiftId, workDate, daySlots)
        }
        if (machineId) {
          const occupant = machineOccupiedByOther(machineId, w.id, allDayEntries)
          if (occupant) {
            const name = occupant.expand?.worker?.name ?? 'другой сотрудник'
            throw new Error(`Станок занят: ${name}`)
          }
        }
        const { machine, scheduleSlot } = resolveMachineFields(machineId)
        if (existing) {
          await pb.collection(COL_TIMESHEET_ENTRIES).update(
            existing.id,
            timesheetEntryUpdatePatch(existing, {
              present: true,
              hours: defaultHours,
              machine,
              scheduleSlot,
            }),
          )
        } else {
          await pb.collection(COL_TIMESHEET_ENTRIES).create(
            buildTimesheetEntryCreate({
              worker: w.id,
              workDate,
              shift: shiftId,
              present: true,
              hours: defaultHours,
              machine,
              scheduleSlot,
              recordedBy: user?.id ?? '',
            }),
          )
        }
      }
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка массового заполнения'))
    } finally {
      setMassSaving(false)
    }
  }

  async function setPresentStatus(w: ProductionWorkerRecord, entry: TimesheetEntryRecord | undefined, present: boolean) {
    if (!canWrite || !shiftId) return
    if (entry && normalizeBool(entry.present, false) === present) return

    const shift = selectedShift
    const defaultHours = shift ? shiftDurationHours(shift.startTime, shift.endTime) : 8
    setErr('')
    try {
      let machineId = entry?.machine || ''
      if (present && !machineId) {
        machineId = suggestDefaultMachineId(w, shiftId, workDate, daySlots)
      }
      if (present && machineId) {
        const occupant = machineOccupiedByOther(machineId, w.id, allDayEntries)
        if (occupant) {
          const name = occupant.expand?.worker?.name ?? 'другой сотрудник'
          setErr(`Станок уже назначен: ${name}`)
          return
        }
      }
      const { machine, scheduleSlot } = present
        ? resolveMachineFields(machineId)
        : { machine: '', scheduleSlot: '' }

      if (present) {
        await clearOtherShiftPresence(w.id, shiftId, allDayEntries)
      }
      const replacement = isReplacementOnShift(w, shiftId)
      const notes =
        present && replacement
          ? 'замена'
          : entry?.notes?.trim() === 'замена' && !replacement
            ? ''
            : entry?.notes

      if (entry) {
        await pb.collection(COL_TIMESHEET_ENTRIES).update(
          entry.id,
          timesheetEntryUpdatePatch(entry, {
            present,
            hours: present ? (entry.hours || defaultHours) : 0,
            machine,
            scheduleSlot,
            ...(notes !== undefined ? { notes: notes ?? '' } : {}),
          }),
        )
      } else {
        await pb.collection(COL_TIMESHEET_ENTRIES).create(
          buildTimesheetEntryCreate({
            worker: w.id,
            workDate,
            shift: shiftId,
            present,
            hours: present ? defaultHours : 0,
            machine,
            scheduleSlot,
            recordedBy: user?.id ?? '',
            notes: replacement && present ? 'замена' : '',
          }),
        )
      }
      setPendingReplacementIds((prev) => prev.filter((id) => id !== w.id))
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, entry ? 'Ошибка обновления' : 'Ошибка создания записи табеля'))
    }
  }

  async function updateMachine(w: ProductionWorkerRecord, entry: TimesheetEntryRecord | undefined, machineId: string) {
    if (!canWrite || !shiftId) return
    setErr('')
    try {
      if (machineId) {
        const occupant = machineOccupiedByOther(machineId, w.id, allDayEntries)
        if (occupant) {
          const name = occupant.expand?.worker?.name ?? 'другой сотрудник'
          setErr(`Станок уже назначен: ${name}`)
          return
        }
      }
      const { machine, scheduleSlot } = resolveMachineFields(machineId)
      const shift = selectedShift
      const defaultHours = shift ? shiftDurationHours(shift.startTime, shift.endTime) : 8

      if (entry) {
        await pb.collection(COL_TIMESHEET_ENTRIES).update(
          entry.id,
          timesheetEntryUpdatePatch(entry, {
            machine,
            scheduleSlot,
          }),
        )
      } else {
        await pb.collection(COL_TIMESHEET_ENTRIES).create(
          buildTimesheetEntryCreate({
            worker: w.id,
            workDate,
            shift: shiftId,
            present: false,
            hours: defaultHours,
            machine,
            scheduleSlot,
            recordedBy: user?.id ?? '',
          }),
        )
      }
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения станка'))
    }
  }

  async function updateHours(entry: TimesheetEntryRecord, hours: number) {
    if (!canWrite) return
    try {
      await pb.collection(COL_TIMESHEET_ENTRIES).update(
        entry.id,
        timesheetEntryUpdatePatch(entry, { hours }),
      )
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка обновления часов'))
    }
  }

  if (loading && workers.length === 0) {
    return <div className={loadingClass}>Загрузка табеля…</div>
  }

  return (
    <div>
      <p className="mb-4 text-sm text-su-text">
        Табель производственных сотрудников. Рабочая бригада 2/2 берётся из календаря цеха; смена подставляется
        автоматически. Станок необязателен; при наличии станка по умолчанию он подставится. На одном станке в смену — один человек.
      </p>
      {timesheetDayHint && <Alert kind="warn">{timesheetDayHint}</Alert>}
      {workingBrigadeId && selectedShift && isRotationShift(selectedShift) && (
        <p className="mb-3 text-sm text-su-text">
          По календарю на этот день работает:{' '}
          <strong>{shiftsById.get(workingBrigadeId)?.name ?? 'бригада 2/2'}</strong>.
        </p>
      )}
      {otherRotationPresentCount > 0 && selectedShift && isRotationShift(selectedShift) && (
        <Alert kind="warn">
          На другой смене 2/2 за этот день отмечено {otherRotationPresentCount}{' '}
          {otherRotationPresentCount === 1 ? 'человек' : 'человека'}. «Заполнить смену» снимет с них явку.
        </Alert>
      )}
      {err && <Alert kind="err">{err}</Alert>}

      <div className={toolbarClass}>
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium text-su-text">Дата</span>
          <input type="date" className={inputClass} value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium text-su-text">Смена</span>
          <select
            className={cn(inputClass, 'min-w-40')}
            value={shiftId}
            onChange={(e) => {
              setShiftTouched(true)
              setShiftId(e.target.value)
            }}
            disabled={!canAllShifts && !!user?.assignedShift}
          >
            <option value="">— выберите —</option>
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <Button type="button" onClick={() => void load()} disabled={loading}>
          Обновить
        </Button>
        {canWrite && replacementCandidates.length > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <span className="font-medium text-su-text">Замена</span>
            <select
              className={cn(inputClass, 'min-w-48')}
              defaultValue=""
              onChange={(e) => {
                const id = e.target.value
                if (!id) return
                setPendingReplacementIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
                e.target.value = ''
              }}
            >
              <option value="">— добавить с другой смены —</option>
              {replacementCandidates.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.expand?.defaultShift?.name ? ` (${w.expand.defaultShift.name})` : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        {canWrite && workingBrigadeId && rotationShiftMismatch && (
          <Button
            type="button"
            onClick={() => {
              setShiftTouched(false)
              setShiftId(workingBrigadeId)
            }}
          >
            Переключить на {shiftsById.get(workingBrigadeId)?.name ?? 'смену из календаря'}
          </Button>
        )}
        {canWrite && (
          <Button
            type="button"
            variant="primary"
            disabled={
              massSaving ||
              !shiftId ||
              !plantWorking ||
              shiftWorkers.length === 0 ||
              Boolean(rotationBrigadeUnset) ||
              rotationShiftMismatch
            }
            onClick={() => void massFillShift()}
          >
            {massSaving ? 'Заполнение…' : 'Заполнить смену'}
          </Button>
        )}
        {entries.length > 0 && (
          <ExportCsvButton filename={`timesheet-${workDate}`} rows={entries} columns={timesheetCsvColumns} />
        )}
      </div>

      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Сотрудник</th>
              <th className={tableHeadClass}>Должность</th>
              <th className={tableHeadClass}>Явка</th>
              <th className={tableHeadClass}>Часы</th>
              <th className={tableHeadClass}>Станок</th>
            </tr>
          </thead>
          <tbody>
            {displayedWorkers.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={5}>
                  <em>{emptyRosterHint}</em>
                </td>
              </tr>
            )}
            {displayedWorkers.map((w) => {
              const entry = entryByWorker.get(w.id)
              const isPresent = entry ? normalizeBool(entry.present, false) : null
              const replacement = Boolean(shiftId && isReplacementOnShift(w, shiftId))
              const homeShift = w.expand?.defaultShift?.name
              const currentMachineId = entry?.machine || w.defaultMachine || ''
              return (
                <tr key={w.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>
                    {w.name}
                    {replacement && (
                      <span className="ml-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
                        замена{homeShift ? ` · ${homeShift}` : ''}
                      </span>
                    )}
                  </td>
                  <td className={tableCellClass}>{w.position ?? '—'}</td>
                  <td className={tableCellClass}>
                    {canWrite ? (
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        {isPresent === null && (
                          <span className="text-xs text-slate-400">не отмечено</span>
                        )}
                        <button
                          type="button"
                          className={btn(isPresent === true ? 'primary' : 'default', 'sm', 'min-w-[6.5rem]')}
                          onClick={() => void setPresentStatus(w, entry, true)}
                          aria-pressed={isPresent === true}
                        >
                          {isPresent === true ? '✓ На смене' : 'На смене'}
                        </button>
                        <button
                          type="button"
                          className={btn(isPresent === false ? 'danger' : 'default', 'sm', 'min-w-[6.5rem]')}
                          onClick={() => void setPresentStatus(w, entry, false)}
                          aria-pressed={isPresent === false}
                        >
                          {isPresent === false ? '✗ Нет' : 'Нет'}
                        </button>
                      </span>
                    ) : entry ? (
                      entry.present ? 'Присутствует' : 'Отсутствует'
                    ) : (
                      <span className="text-su-text">—</span>
                    )}
                  </td>
                  <td className={tableCellClass}>
                    {entry && canWrite ? (
                      <input
                        type="number"
                        min={0}
                        max={24}
                        step={0.5}
                        className={cn(inputClass, 'max-w-20 py-1')}
                        defaultValue={entry.hours ?? ''}
                        onBlur={(e) => {
                          const v = Number(e.target.value)
                          if (Number.isFinite(v) && v !== entry.hours) void updateHours(entry, v)
                        }}
                      />
                    ) : (
                      entry?.hours != null ? formatQty(entry.hours) : '—'
                    )}
                  </td>
                  <td className={tableCellClass}>
                    {canWrite ? (
                      <select
                        className={cn(inputClass, 'min-w-36 py-1')}
                        value={entry?.machine || currentMachineId || ''}
                        onChange={(e) => void updateMachine(w, entry, e.target.value)}
                      >
                        <option value="">— станок —</option>
                        {machines.map((m) => (
                          <option key={m.id} value={m.id}>
                            {formatMachineLabel(m)}
                          </option>
                        ))}
                      </select>
                    ) : entry?.expand?.machine ? (
                      formatMachineLabel(entry.expand.machine)
                    ) : w.expand?.defaultMachine ? (
                      formatMachineLabel(w.expand.defaultMachine)
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
